import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { clearToken, getToken } from './auth.service';

type ApiErrorHandler = (error: ApiError) => void;

type EventHandler = ApiErrorHandler;

interface EventHandlers {
  error: ApiErrorHandler;
}

export class ApiError extends Error {
  __proto__: ApiError;

  data: unknown;

  status: number;

  /**
   * True only when the request never got a response at all (connection
   * refused/dropped, CORS failure, DNS/timeout) -- as opposed to the server
   * responding with an error status. handleApiError needs this distinction
   * explicitly: a bare error.message like "503 Network Error" leaking to a
   * toast reads as a real, deliberate backend response when it's actually
   * just "couldn't reach the server", and guessing from the status number
   * alone isn't reliable since the app does have real backend endpoints
   * that deliberately return 503.
   */
  isNetworkError: boolean;

  constructor(data: unknown, status = 500, statusText = 'Internal Server Error', isNetworkError = false) {
    super(`${status} ${statusText}`);

    this.constructor = ApiError;
    this.__proto__ = ApiError.prototype;

    this.name = this.constructor.name;
    this.data = data;
    this.status = status;
    this.isNetworkError = isNetworkError;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  inspect() {
    return this.stack;
  }
}

interface ThrowApiErrorProps {
  status: number;
  statusText: string;
  data: unknown;
}

class ApiClient {
  _api: AxiosInstance;

  private _handlers: Map<keyof EventHandlers, Set<EventHandler>>;

  constructor(axiosConfig: AxiosRequestConfig) {
    this._handlers = new Map<keyof EventHandlers, Set<EventHandler>>();
    this._api = axios.create(axiosConfig);

    this._api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this._api.interceptors.response.use(
      (response: AxiosResponse) => response.data,
      (error) => {
        const fallbackStatus = (): number => {
          const c = error.code as string | undefined;
          if (c === 'ECONNABORTED') {
            return 408;
          }
          if (c && /^\d{3}$/.test(c)) {
            return parseInt(c, 10);
          }
          return 503;
        };

        const hasRealResponse = Boolean(error.response);

        const errorResponse: ThrowApiErrorProps = error.response || {
          status: fallbackStatus(),
          statusText:
            (typeof error.message === 'string' && error.message !== ''
              ? error.message
              : null) || 'Network or timeout error',
          data: error.data,
        };

        if (errorResponse.status === 401) {
          clearToken();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        const apiError = new ApiError(
          errorResponse.data,
          errorResponse.status,
          errorResponse.statusText,
          !hasRealResponse,
        );

        const errorHandlers = this._handlers.get('error') as Set<ApiErrorHandler>;

        errorHandlers?.forEach((handler) => handler(apiError));

        throw apiError;
      },
    );
  }

  get<T, P>(url: string, params: P | unknown = {}, requestConfig: AxiosRequestConfig = {}): Promise<T> {
    return this._api({
      method: 'get',
      url,
      params,
      ...requestConfig,
    });
  }

  post<T, D>(url: string, data: D | unknown = {}, requestConfig: AxiosRequestConfig = {}): Promise<T> {
    return this._api({
      method: 'post',
      url,
      data,
      ...requestConfig,
    });
  }

  put<T, D>(url: string, data: D | unknown = {}, requestConfig: AxiosRequestConfig = {}): Promise<T> {
    return this._api({
      method: 'put',
      url,
      data,
      ...requestConfig,
    });
  }

  patch<T, D>(url: string, data: D | unknown = {}, requestConfig: AxiosRequestConfig = {}): Promise<T> {
    return this._api({
      method: 'patch',
      url,
      data,
      ...requestConfig,
    });
  }

  delete<T, D>(url: string, data: D | unknown = {}, requestConfig: AxiosRequestConfig = {}): Promise<T> {
    return this._api({
      method: 'delete',
      url,
      data,
      ...requestConfig,
    });
  }

  on<T extends keyof EventHandlers>(event: T, handler: EventHandlers[T]): void {
    let handlers = this._handlers.get(event);

    if (!handlers) {
      handlers = new Set<EventHandler>();

      this._handlers.set(event, handlers);
    }

    handlers.add(handler as EventHandler);
  }
}

export default new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  withCredentials: true,
  responseType: 'json',
});
